/**
 * $api service layer for the human-facing oneData dataset UI.
 *
 * Every dataset space is one SQLite database: a table is a table in it, and a
 * row is addressed by its `rowid` (`{ id, data }`). Tables + rows are exposed as
 * space-scoped endpoints under `/api/spaces/{spaceId}/tables…` (mirroring
 * `/api/spaces/{spaceId}/files…`).
 */

import { $api } from "@/utils/fetch.ts";
import type {
	GetNextPageParamFunction,
	QueryClient,
} from "@tanstack/react-query";

/** Query options: the data tables in a space (columns + row counts) for cards. */
export const spaceTablesQueryOptions = (spaceId: string) =>
	$api.queryOptions("get", "/api/spaces/{spaceId}/tables", {
		params: { path: { spaceId } },
	});

/** The agents that use a dataset space (reads, delivers, appends, triggered), paged. */
export const useSpaceAgentsInfiniteQuery = (spaceId: string) =>
	$api.useInfiniteQuery(
		"get",
		"/api/spaces/{spaceId}/agents",
		{ params: { path: { spaceId } } },
		{
			pageParamName: "page",
			initialPageParam: 0,
			getNextPageParam: (lastPage, _allPages, lastPageParam) =>
				lastPage.hasNextPage && typeof lastPageParam === "number"
					? lastPageParam + 1
					: undefined,
		},
	);

const getNextRowPageParam: GetNextPageParamFunction<
	unknown,
	{
		rows: { id: number | null; data: Record<string, unknown> }[];
		totalCount: number;
	}
> = (lastPage, allPages, lastPageParam) => {
	const loadedCount = allPages.reduce(
		(sum, page) => sum + (page.rows?.length ?? 0),
		0,
	);
	if (loadedCount < lastPage.totalCount && typeof lastPageParam === "number") {
		return lastPageParam + 1;
	}
	return undefined;
};

/** Page-based infinite query over a table's rows (mirrors files list). */
export const useSpaceTableRowsInfiniteQuery = ({
	spaceId,
	tableName,
	pageSize = 50,
}: {
	spaceId: string;
	tableName: string;
	pageSize?: number;
}) =>
	$api.useInfiniteQuery(
		"get",
		"/api/spaces/{spaceId}/tables/{tableName}/rows",
		{
			params: {
				path: { spaceId, tableName },
				query: { pageSize },
			},
		},
		{
			pageParamName: "page",
			getNextPageParam: getNextRowPageParam,
			initialPageParam: 0,
		},
	);

export const useCreateTableMutation = () =>
	$api.useMutation("post", "/api/spaces/{spaceId}/tables");

export const useAddRowMutation = () =>
	$api.useMutation("post", "/api/spaces/{spaceId}/tables/{tableName}/rows");

export const useUpdateRowMutation = () =>
	$api.useMutation(
		"put",
		"/api/spaces/{spaceId}/tables/{tableName}/rows/{rowId}",
	);

export const useDeleteRowsMutation = () =>
	$api.useMutation("delete", "/api/spaces/{spaceId}/tables/{tableName}/rows");

/** Invalidate the tables list for a space (after create table / row-count change). */
export const invalidateSpaceTables = (
	queryClient: QueryClient,
	spaceId: string,
): void => {
	queryClient.invalidateQueries({
		predicate: (query) => {
			const key = query.queryKey;
			return (
				Array.isArray(key) &&
				key[0] === "get" &&
				key[1] === "/api/spaces/{spaceId}/tables" &&
				JSON.stringify(key).includes(spaceId)
			);
		},
	});
};

/** Invalidate the row list for a space's tables (after an inline add/edit/delete). */
export const invalidateSpaceTableRows = (
	queryClient: QueryClient,
	spaceId: string,
): void => {
	queryClient.invalidateQueries({
		predicate: (query) => {
			const key = query.queryKey;
			return (
				Array.isArray(key) &&
				key[0] === "get" &&
				key[1] === "/api/spaces/{spaceId}/tables/{tableName}/rows" &&
				JSON.stringify(key).includes(spaceId)
			);
		},
	});
};

/**
 * CSV export of a whole table, as a blob. The endpoint responds with raw bytes
 * (`y.raw()`), so this is a `parseAs: "blob"` query fetched imperatively via
 * `queryClient.fetchQuery` and handed to a download anchor — the same shape the
 * audit-log export uses. `staleTime: 0` because a table's rows change under it
 * and a cached blob would silently hand back a stale file.
 */
export const spaceTableExportCsvQueryOptions = (
	spaceId: string,
	tableName: string,
) =>
	$api.queryOptions(
		"get",
		"/api/spaces/{spaceId}/tables/{tableName}/export-csv",
		{
			params: { path: { spaceId, tableName } },
			parseAs: "blob",
		},
		{ staleTime: 0, gcTime: 0 },
	);

/**
 * CSV import (upload-only: the raw CSV text is the request body). Callers must
 * pass `bodySerializer: () => text` alongside the `text/csv` content-type so
 * openapi-fetch sends the bytes verbatim instead of JSON-encoding them (mirrors
 * the code-editor's raw file upload).
 */
export const useImportCsvMutation = () =>
	$api.useMutation(
		"post",
		"/api/spaces/{spaceId}/tables/{tableName}/import-csv",
	);
